import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Truck, PackageCheck, Wrench } from "lucide-react";
import { IMAGES } from "../mock";
import Seo from "../components/Seo";

const TrailerKit = () => {
  return (
    <div className="bg-[#0E0E0F] text-[#EDE3CB]">
      <Seo
        path="/trailer-kit"
        title="48' Property Trailer + Kit"
        description="A 48-foot property trailer with a comprehensive kit, available as part of Jesse J. Adams' Property Master package. Production-ready storage, prep + wrap support, and travel-ready infrastructure."
      />
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.trailer} alt="48 foot property trailer" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0E0E0F]/55 via-[#0E0E0F]/40 to-[#0E0E0F]" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-16">
          <div className="section-label">Trailer + Kit</div>
          <h1 className="font-display text-6xl md:text-9xl mt-4 leading-[0.85]">48' <span className="brass-text">PROPERTY TRAILER</span></h1>
          <p className="font-serif-accent italic text-2xl text-[#EDE3CB]/85 mt-6 max-w-2xl">
            A production-ready property department infrastructure available as part of my Property Master package.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="tickerstrip">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { k: "48'", v: "Trailer Length", i: Truck },
            { k: "Full Kit", v: "Comprehensive Inventory", i: PackageCheck },
            { k: "Travel-Ready", v: "Available Worldwide", i: MapPin },
            { k: "Fab Stations", v: "Tools + 3D Printing", i: Wrench },
          ].map((s) => (
            <div key={s.v} className="flex items-start gap-4">
              <s.i className="text-[#B8935A] mt-1" size={22} strokeWidth={1.4} />
              <div>
                <div className="font-display text-2xl">{s.k}</div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#EDE3CB]/55 mt-1">{s.v}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why it helps */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="section-label">Why It Helps Production</div>
          <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">A stable base of <span className="brass-text">operations</span>.</h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 space-y-5 text-[#EDE3CB]/80 text-lg leading-relaxed">
          <p>
            A production-ready property trailer built to keep the department organized, efficient, and responsive from prep through wrap. Shelving, labeled bins, and category-based zones allow for fast access, while dedicated workspace supports breakdowns, fabrication, paper props, and clean wrap-out. With tools, supplies, specialty stock, and a comprehensive kit close at hand, the trailer reduces rental needs, cuts down on last-minute runs, and provides a stable base of operations.
          </p>
          <p className="text-[#EDE3CB]/60 italic font-serif-accent text-base">
            Available as part of my Property Master package depending on production needs, location, and schedule.
          </p>
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-24">
        <div className="section-label mb-6">Gallery</div>
        <h2 className="font-display text-4xl md:text-5xl mb-10 leading-[0.95]">Inside the kit.</h2>
        <div className="border border-[#B8935A]/25 bg-[#0B0B0C] py-20 md:py-28 px-6 text-center">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#B8935A]">Photo Gallery</div>
          <div className="font-display text-5xl md:text-6xl mt-4 tracking-wide">Coming Soon</div>
          <p className="font-serif-accent italic text-lg md:text-xl text-[#EDE3CB]/65 mt-5 max-w-xl mx-auto leading-snug">
            Trailer interiors, kit details, and behind-the-scenes shots from set are on the way.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-28">
        <div className="border border-[#B8935A]/30 p-10 md:p-16 text-center">
          <div className="section-label">Inquiries</div>
          <h3 className="font-display text-4xl md:text-6xl mt-4 leading-[0.95] max-w-3xl mx-auto">Add a department on day one.</h3>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/contact" className="btn-primary">Check Availability <ArrowUpRight size={14} /></Link>
            <Link to="/services" className="btn-secondary">The Prop Department</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrailerKit;
